/**
 * QuickMeet Tier System - Core utility functions for 3-tier subscription model
 *
 * Tiers:
 * - Free: $0/month - Limited features, basic functionality
 * - Pro: $5.50 AUD/month - Enhanced features, personal use
 * - Team: $12 USD/user/month (min 5 seats) - Organization features, collaboration
 */

export const TIERS = {
  FREE: 'free',
  PRO: 'pro',
  TEAM: 'team'
};

/**
 * Feature limits by tier
 * -1 = unlimited
 */
export const TIER_LIMITS = {
  free: {
    scheduledMeetings: 3,
    meetingNotesRetention: 5, // Last N meetings
    activePolls: 1,
    pollOptions: 2, // Yes/No only
    breakoutRooms: 0,
    recordings: 1,
    storageGB: 1,
    maxMeetingDuration: 60, // minutes
    participantsPerMeeting: 10,
    customBackgrounds: 3,
    chatFileSize: 5, // MB (limited file sharing)
    cloudIntegrations: 0,
    personalMeetingRoom: false,
    // Chat limits
    chatHistoryDays: 7,
    activeGroupChats: 0,
    meetingChatPersistent: false,
  },
  pro: {
    scheduledMeetings: 50,
    meetingNotesRetention: -1, // unlimited
    activePolls: 10,
    pollOptions: 10,
    breakoutRooms: 10,
    recordings: 10,
    storageGB: 100,
    maxMeetingDuration: 240, // 4 hours
    participantsPerMeeting: 50,
    customBackgrounds: -1, // unlimited
    chatFileSize: 100, // MB
    cloudIntegrations: 3,
    personalMeetingRoom: true,
    // Chat limits
    chatHistoryDays: 90,
    activeGroupChats: 5,
    meetingChatPersistent: true,
  },
  team: {
    scheduledMeetings: -1, // unlimited
    meetingNotesRetention: -1, // unlimited
    activePolls: -1, // unlimited
    pollOptions: 20,
    breakoutRooms: 50,
    recordings: -1, // unlimited
    storageGB: 1000,
    maxMeetingDuration: -1, // unlimited
    participantsPerMeeting: 100,
    customBackgrounds: -1, // unlimited
    chatFileSize: 100, // MB
    cloudIntegrations: -1, // unlimited
    personalMeetingRoom: true,
    // Chat limits
    chatHistoryDays: -1, // unlimited
    activeGroupChats: -1, // unlimited
    meetingChatPersistent: true,
  }
};

/**
 * Feature availability map
 * Maps feature names to the minimum tier required
 */
const FEATURE_MAP = {
  // Free tier features (available to all)
  basicMeetings: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  basicChat: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  basicScreenShare: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  basicRecording: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  basicPolls: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  basicScheduler: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  meetingNotesViewer: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  basicReactions: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  reactionsSummary: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  basicAnnotation: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  waitingRoomMusic: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  meetingEndSurvey: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],

  // Chat features (tiered)
  directMessages: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  meetingChat: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  persistentMeetingChat: [TIERS.PRO, TIERS.TEAM],
  groupChats: [TIERS.PRO, TIERS.TEAM],
  orgChannels: [TIERS.TEAM],
  chatSearch: [TIERS.PRO, TIERS.TEAM],

  // Pro tier features
  personalMeetingRoom: [TIERS.PRO, TIERS.TEAM],
  advancedRecording: [TIERS.PRO, TIERS.TEAM],
  recordingEditor: [TIERS.PRO, TIERS.TEAM],
  aiAssistant: [TIERS.PRO, TIERS.TEAM],
  aiTranscription: [TIERS.PRO, TIERS.TEAM],
  aiSummary: [TIERS.PRO, TIERS.TEAM],
  aiActionItems: [TIERS.PRO, TIERS.TEAM],
  advancedAnalytics: [TIERS.PRO, TIERS.TEAM],
  cloudStorageIntegration: [TIERS.PRO, TIERS.TEAM],
  breakoutRooms: [TIERS.PRO, TIERS.TEAM],
  customThemes: [TIERS.PRO, TIERS.TEAM],
  prioritySupport: [TIERS.PRO, TIERS.TEAM],
  betaFeatures: [TIERS.PRO, TIERS.TEAM],
  advancedPolls: [TIERS.PRO, TIERS.TEAM],
  pollAnalytics: [TIERS.PRO, TIERS.TEAM],
  meetingNotesEditor: [TIERS.PRO, TIERS.TEAM],
  chatFileSharing: [TIERS.FREE, TIERS.PRO, TIERS.TEAM],
  customBackgrounds: [TIERS.PRO, TIERS.TEAM],

  // Team tier features (organization only)
  organizationWorkspace: [TIERS.TEAM],
  teamAdminPanel: [TIERS.TEAM],
  memberManagement: [TIERS.TEAM],
  roleBasedPermissions: [TIERS.TEAM],
  sharedResourcesLibrary: [TIERS.TEAM],
  teamIntegrations: [TIERS.TEAM],
  slackIntegration: [TIERS.TEAM],
  teamsIntegration: [TIERS.TEAM],
  jiraIntegration: [TIERS.TEAM],
  salesforceIntegration: [TIERS.TEAM],
  zapierIntegration: [TIERS.TEAM],
  complianceDashboard: [TIERS.TEAM],
  auditLogs: [TIERS.TEAM],
  dataResidency: [TIERS.TEAM],
  ssoIntegration: [TIERS.TEAM],
  teamWhiteboard: [TIERS.TEAM],
  sharedCalendar: [TIERS.TEAM],
  teamAnalytics: [TIERS.TEAM],
  webhooks: [TIERS.TEAM],
  apiAccess: [TIERS.TEAM],
  embedMeeting: [TIERS.TEAM],
  customBranding: [TIERS.TEAM],

  // Platform features (tier-dependent limits)
  enhancedChat: [TIERS.PRO, TIERS.TEAM],
  chatThreads: [TIERS.PRO, TIERS.TEAM],
  chatMentions: [TIERS.PRO, TIERS.TEAM],
  advancedBackgrounds: [TIERS.PRO, TIERS.TEAM],
  videoBackgrounds: [TIERS.PRO, TIERS.TEAM],
  greenScreen: [TIERS.PRO, TIERS.TEAM],
  liveCaptions: [TIERS.PRO, TIERS.TEAM],
  togetherMode: [TIERS.TEAM],
  focusMode: [TIERS.PRO, TIERS.TEAM],
  spatialAudio: [TIERS.PRO, TIERS.TEAM],
  audioEQ: [TIERS.PRO, TIERS.TEAM],
  smartScheduling: [TIERS.PRO, TIERS.TEAM],
  liveQA: [TIERS.PRO, TIERS.TEAM],
  wordCloud: [TIERS.PRO, TIERS.TEAM],
  moodTracker: [TIERS.PRO, TIERS.TEAM],
};

/**
 * Check if a user's tier has access to a specific feature
 * @param {string} tier - User's subscription tier ('free', 'pro', 'team')
 * @param {string} feature - Feature name to check
 * @returns {boolean} Whether the tier has access to the feature
 */
export const hasFeature = (tier, feature) => {
  if (!tier || !feature) return false;

  const allowedTiers = FEATURE_MAP[feature];
  if (!allowedTiers) {
    console.warn(`Unknown feature: ${feature}`);
    return false;
  }

  return allowedTiers.includes(tier);
};

/**
 * Check if a user can use a feature based on their profile
 * Accounts for both tier access and organization membership
 * @param {object} userProfile - User profile object with subscriptionTier and organizationId
 * @param {string} feature - Feature name to check
 * @returns {boolean} Whether the user can use the feature
 */
export const canUseFeature = (userProfile, feature) => {
  if (!userProfile) return false;

  const tier = userProfile.subscriptionTier || TIERS.FREE;

  // Team features require organization membership
  const isTeamFeature = FEATURE_MAP[feature]?.length === 1 && FEATURE_MAP[feature][0] === TIERS.TEAM;
  if (isTeamFeature) {
    return tier === TIERS.TEAM && !!userProfile.organizationId;
  }

  return hasFeature(tier, feature);
};

/**
 * Check if current usage is within tier limits
 * @param {object} userProfile - User profile object with subscriptionTier and usage data
 * @param {string} limitType - Type of limit to check (matches keys in TIER_LIMITS)
 * @param {number} currentUsage - Current usage count
 * @returns {object} { allowed: boolean, remaining: number, limit: number }
 */
export const checkLimit = (userProfile, limitType, currentUsage = 0) => {
  const tier = userProfile?.subscriptionTier || TIERS.FREE;
  const limit = TIER_LIMITS[tier]?.[limitType];

  if (limit === undefined) {
    console.warn(`Unknown limit type: ${limitType}`);
    return { allowed: true, remaining: -1, limit: -1 };
  }

  // -1 means unlimited
  if (limit === -1) {
    return { allowed: true, remaining: -1, limit: -1 };
  }

  // Boolean limits
  if (typeof limit === 'boolean') {
    return { allowed: limit, remaining: limit ? 1 : 0, limit: limit ? 1 : 0 };
  }

  // Numeric limits
  const allowed = currentUsage < limit;
  const remaining = Math.max(0, limit - currentUsage);

  return { allowed, remaining, limit };
};

/**
 * Get upgrade message for a specific feature
 * @param {string} currentTier - User's current tier
 * @param {string} feature - Feature they're trying to access
 * @returns {object} { requiredTier: string, message: string }
 */
export const getUpgradeMessage = (currentTier, feature) => {
  const allowedTiers = FEATURE_MAP[feature];

  if (!allowedTiers || allowedTiers.length === 0) {
    return { requiredTier: null, message: 'This feature is not available.' };
  }

  // If user already has access
  if (allowedTiers.includes(currentTier)) {
    return { requiredTier: null, message: null };
  }

  // Determine which tier to upgrade to
  const requiredTier = allowedTiers.includes(TIERS.PRO) ? TIERS.PRO : TIERS.TEAM;

  const messages = {
    [TIERS.PRO]: 'Upgrade to Pro to unlock this feature.',
    [TIERS.TEAM]: 'This feature is only available for Team organizations.'
  };

  return { requiredTier, message: messages[requiredTier] };
};

/**
 * Get a formatted tier display name
 * @param {string} tier - Tier key
 * @returns {string} Display name
 */
export const getTierDisplayName = (tier) => {
  const names = {
    [TIERS.FREE]: 'Free',
    [TIERS.PRO]: 'Pro',
    [TIERS.TEAM]: 'Team'
  };

  return names[tier] || 'Unknown';
};

/**
 * Get tier pricing information
 * @param {string} tier - Tier key
 * @returns {object} { price: number, currency: string, interval: string, minSeats: number }
 */
export const getTierPricing = (tier) => {
  const pricing = {
    [TIERS.FREE]: { price: 0, currency: 'AUD', interval: 'month', minSeats: 1 },
    [TIERS.PRO]: { price: 5.50, currency: 'AUD', interval: 'month', minSeats: 1 },
    [TIERS.TEAM]: { price: 12, currency: 'USD', interval: 'month', minSeats: 5, perUser: true }
  };

  return pricing[tier] || pricing[TIERS.FREE];
};

/**
 * Check if a tier is premium (Pro or Team)
 * @param {string} tier - Tier to check
 * @returns {boolean} Whether the tier is premium
 */
export const isPremiumTier = (tier) => {
  return tier === TIERS.PRO || tier === TIERS.TEAM;
};

/**
 * Get all features available for a tier
 * @param {string} tier - Tier to check
 * @returns {string[]} Array of feature names available for the tier
 */
export const getFeaturesForTier = (tier) => {
  return Object.keys(FEATURE_MAP).filter(feature => hasFeature(tier, feature));
};

/**
 * Legacy compatibility: Convert old subscriptionStatus to subscriptionTier
 * @param {string} subscriptionStatus - Old status ('free' or 'active')
 * @returns {string} New tier ('free' or 'pro')
 */
export const migrateSubscriptionStatus = (subscriptionStatus) => {
  if (subscriptionStatus === 'active') return TIERS.PRO;
  return TIERS.FREE;
};
